import {useEffect, useMemo, useRef} from 'react';
import 'leaflet/dist/leaflet.css';
import {MapContainer, TileLayer, Marker, Popup, useMap} from 'react-leaflet';
import L, {LatLngBoundsExpression, LatLngExpression} from 'leaflet';
import {
  RouteTrackingDetails,
  RouteTrackingDelivery,
} from 'types/route-tracking';
import styles from './RouteTrackingMap.module.scss';
import './RouteTrackingMap.css';

type Props = {
  route: RouteTrackingDetails;
};

type Location = {
  lat: number;
  lng: number;
};

function DrawPolylines({
  polylines,
  routeColor,
}: {
  polylines: LatLngExpression[];
  routeColor: string;
}) {
  const map = useMap();
  const polylineRef = useRef<any>(null);

  useEffect(() => {
    if (!map || !polylines || polylines.length === 0) return;

    polylineRef.current = L.polyline(polylines, {
      color: routeColor || '#4285f4',
      opacity: 0.8,
      weight: 4,
    })?.addTo(map);

    map?.fitBounds(polylines as LatLngBoundsExpression);
    map?.invalidateSize();

    return () => {
      if (
        polylineRef.current &&
        typeof polylineRef.current.remove === 'function'
      ) {
        polylineRef.current.remove();
        polylineRef.current = null;
      }
    };
  }, [map, polylines, routeColor]);

  return null;
}

function FitBounds({locations}: {locations: Location[]}) {
  const map = useMap();

  useEffect(() => {
    if (locations.length === 0) return;

    const points: L.LatLngExpression[] = locations.map((loc) => [
      loc.lat,
      loc.lng,
    ]);
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, {padding: [25, 25]});
  }, [map, locations]);

  return null;
}

const getLocationMarkerIcon = (
  label: string,
  color: string = '#4285f4'
): L.DivIcon => {
  return new L.DivIcon({
    className: 'leaflet-div-icon',
    html: `<div class="location-marker" style="background-color: ${color}">
      <span>${label}</span>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });
};

const getDeliveryMarkerIcon = (
  number: number,
  status: 'new' | 'delivered' | 'skipped'
): L.DivIcon => {
  let color = '#4285f4';
  let icon = `
    <svg
      width="16"
      height="16"
      viewbox="0 0 24 24"
      fill="none"
      stroke="white"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" fill="none"/>
      <path d="M12 22V12" fill="none"/>
      <polyline points="3.29 7 12 12 20.71 7" fill="none"/>
      <path d="m7.5 4.27 9 5.15" fill="none"/>
    </svg>`;

  if (status === 'delivered') {
    color = '#22c55e';
    icon = `
      <svg
        width="16"
        height="16"
        viewbox="0 0 24 24"
        fill="white"
        stroke-width="2"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
          fill="white"
        />
      </svg>`;
  } else if (status === 'skipped') {
    color = '#ef4444';
    icon = `
      <svg
        width="16"
        height="16"
        viewbox="0 0 24 24"
        fill="white"
        stroke-width="2"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
          fill="white"
        />
      </svg>
    `;
  }

  return new L.DivIcon({
    className: 'leaflet-div-icon',
    html: `<div class="delivery-marker" style="background-color: ${color}">
      <span>${number}</span>
      ${icon}
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
};

const getDeliveryStatus = (
  delivery: RouteTrackingDelivery
): 'new' | 'delivered' | 'skipped' => {
  if (delivery.delivered_at) return 'delivered';
  if (delivery.skip_reason) return 'skipped';
  return 'new';
};

function RouteTrackingMap({route}: Props) {
  const driverLocation: Location = useMemo(
    () => ({
      lat: route.driver.latitude,
      lng: route.driver.longitude,
    }),
    [route.driver.latitude, route.driver.longitude]
  );

  const startLocation: Location = useMemo(
    () => ({
      lat: route.start_latitude,
      lng: route.start_longitude,
    }),
    [route.start_latitude, route.start_longitude]
  );

  const endLocation: Location = useMemo(
    () => ({
      lat: route.end_latitude,
      lng: route.end_longitude,
    }),
    [route.end_latitude, route.end_longitude]
  );

  // Extract delivery locations from polylines if available
  const deliveryLocations = useMemo(() => {
    if (!route.polylines || route.polylines.length === 0) {
      return [];
    }

    // Polylines contains the route coordinates
    // We'll distribute delivery markers evenly along the route
    const totalDeliveries = route.deliveries.length;
    if (totalDeliveries === 0) return [];

    const polylinePoints = route.polylines;
    const pointsPerDelivery = Math.floor(
      polylinePoints.length / (totalDeliveries + 1)
    );

    const locations: Location[] = [];
    for (let i = 1; i <= totalDeliveries; i++) {
      const pointIndex = Math.min(
        i * pointsPerDelivery,
        polylinePoints.length - 1
      );
      const point = polylinePoints[pointIndex];

      // Handle both array and object formats
      if (Array.isArray(point)) {
        locations.push({lat: point[0] as number, lng: point[1] as number});
      } else if (
        typeof point === 'object' &&
        'lat' in point &&
        'lng' in point
      ) {
        locations.push({
          lat: (point as any).lat,
          lng: (point as any).lng,
        });
      }
    }

    return locations;
  }, [route.polylines, route.deliveries.length]);

  const allLocations = useMemo(() => {
    return [driverLocation, startLocation, endLocation, ...deliveryLocations];
  }, [driverLocation, startLocation, endLocation, deliveryLocations]);

  const center: L.LatLngExpression = useMemo(() => {
    const avgLat =
      (driverLocation.lat + startLocation.lat + endLocation.lat) / 3;
    const avgLng =
      (driverLocation.lng + startLocation.lng + endLocation.lng) / 3;
    return [avgLat, avgLng];
  }, [driverLocation, startLocation, endLocation]);

  return (
    <div className={styles.mapContainer}>
      <MapContainer
        center={center}
        style={{height: '100%', width: '100%'}}
        zoom={12}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>'
        />

        {route.polylines && route.polylines.length > 0 ? (
          <DrawPolylines polylines={route.polylines} routeColor={route.color} />
        ) : (
          <FitBounds locations={allLocations} />
        )}

        <Marker
          position={[startLocation.lat, startLocation.lng]}
          icon={getLocationMarkerIcon('S', '#22c55e')}
        >
          <Popup>
            <div className={styles.popup}>
              <strong>Start Location</strong>
              <p>{route.start_location}</p>
            </div>
          </Popup>
        </Marker>

        {route.deliveries.map((delivery, index) => {
          const location = deliveryLocations[index];
          if (!location) return null;

          const status = getDeliveryStatus(delivery);

          return (
            <Marker
              key={delivery.number}
              position={[location.lat, location.lng]}
              icon={getDeliveryMarkerIcon(delivery.number, status)}
            />
          );
        })}

        {/* <Marker
          position={[driverLocation.lat, driverLocation.lng]}
          icon={getDriverIcon()}
        >
          <Popup>
            <div className={styles.popup}>
              <strong>Driver: {route.driver.full_name}</strong>
              <p>Current location</p>
            </div>
          </Popup>
        </Marker> */}

        <Marker
          position={[endLocation.lat, endLocation.lng]}
          icon={getLocationMarkerIcon('E', '#ef4444')}
        >
          <Popup>
            <div className={styles.popup}>
              <strong>End Location</strong>
              <p>{route.end_location}</p>
            </div>
          </Popup>
        </Marker>
      </MapContainer>
    </div>
  );
}

export default RouteTrackingMap;
